import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { applicationService } from '../../../api/services/application.service';
import Button from '@mui/material/Button';

import DealsTableSearch from './table/DealsTableSearch';
import { DealOverviewDto } from '../../../api/services/dto/deal-overview-dto';
import DealsTable from './table/DealsTable';
import { DEAL_SEARCH_BY_FIELD, GetDealsQueryParams } from '../../../util/deals-util';
import { useAlert } from '../../shared/AlertProvider';
import { ColumnKey } from './table/ColumnVisibilityButton';
import Card from '@mui/material/Card';
import { useWorkspace } from '../../../context/WorkspaceContext';

const ROWS_PER_PAGE_INIT: number = 25;
const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = ['ssp', 'buyerSeatName', 'buyerSeatId', 'dealId', 'name', 'floorPrice', 'mediaType', 'status', 'created', 'actions'];

const List = () => {
  const { setAlert } = useAlert();
  const [deals, setDeals] = useState<DealOverviewDto[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_INIT);

  const [search, setSearch] = useState<string>('');
  const [searchByField, setSearchByField] = useState<DEAL_SEARCH_BY_FIELD>(DEAL_SEARCH_BY_FIELD.NAME);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);

  const navigate = useNavigate();
  const { workspace } = useWorkspace();

  const fetch = (params: GetDealsQueryParams) =>
    applicationService
      .getDeals(params)
      .then((res) => {
        setDeals(res.resultList);
        setTotal(res.totalItems);
      })
      .catch((reason) =>
        setAlert({
          open: true,
          severity: 'error',
          title: 'Failed to fetch deals',
          message: reason.message,
        })
      );

  useEffect(() => {
    if (!workspace) return;
    const queryParams: GetDealsQueryParams = {
      page,
      size: rowsPerPage,
      searchByValue: search || undefined,
      searchByField: searchByField || undefined,
      sort: 'auditMetadata.createdDate,desc',
    };
    let timer: NodeJS.Timeout | undefined;
    if (search && searchByField) {
      // This would be nicer:
      // https://adityasaxena.hashnode.dev/how-to-create-a-custom-debounce-react-hook-using-useeffect
      timer = setTimeout(() => fetch(queryParams), 500);
    } else {
      fetch(queryParams);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [page, rowsPerPage, search, searchByField, workspace]);

  const handleSearchStringChange = (searchStr: string, searchByField: DEAL_SEARCH_BY_FIELD) => {
    setSearchByField(searchByField);
    setSearch(searchStr);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleColumnVisibilityChange = (columns: ColumnKey[]) => {
    setVisibleColumns(columns);
  };

  return (
    <PageWrapper>
      <ButtonWrapper>
        <Button color="primary" variant="contained" size="large" onClick={() => navigate('/deal/new')}>
          Create Deal
        </Button>
      </ButtonWrapper>

      <SearchWrapper>

        <DealsTableSearch
          search={search}
          searchByColumns={searchByField}
          onSearchStringChange={handleSearchStringChange}
          parentComponent="buyer-pro-table"
          visibleColumns={visibleColumns}
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />
      </SearchWrapper>

      <DealsTable
        dealList={deals}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        visibleColumns={visibleColumns}
      />
    </PageWrapper>
  );
};

export default List;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  height: auto;
  background-color: #FFFFFF;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;
  padding: 16px 16px 0;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;
  gap: 16px;
  padding: 0 16px;
`;
